/**
 * Zipchat AI Search handoff for the header search field.
 *
 * The visitor types in the theme's own field and submits explicitly — Enter, the
 * magnifier, or a form submit. The query then goes to Zipchat's AI Search, which
 * opens directly onto its results card. The widget's own search bar is skipped:
 * seeding the query in the same task as the trigger click hides the pill
 * synchronously, before the overlay's fade-in frame, so it never paints.
 *
 * Nothing here runs unless AI Search is actually live on this device — see
 * searchActive(). With it off, the field behaves like a plain search form.
 *
 * Interim implementation: it drives the overlay's internals because the widget
 * exposes no public "open with this query" entry point. When
 * Zipchat.openSearch(query) ships, handoff() collapses to that one call.
 * Depends on: window.Zipchat._shadowRoot, el._zipchatSearchHijacked,
 * #zipchat-search-overlay, .zipchat-search-input, .zipchat-search-modal-input,
 * .zipchat-search-modal-wrapper.
 */
(function () {
  'use strict';

  var TRIGGER_ID = 'zipchat-search-trigger';
  var FIELD_SCOPE = '[data-zipchat-search], .header__search';
  var FIELD_SELECTOR = 'input[name="q"], input[type="search"]';
  var GLOW_CLASS = 'zipchat-is-focused';

  // Matches the overlay's own floor: submitSearch ignores anything shorter.
  var MIN_QUERY = 2;

  // Debugging: localStorage.setItem('zipchat_search_debug', '1') then reload.
  // localStorage rather than a window flag, because a failed handoff can end in
  // a navigation that wipes both the console and any window state.
  function debugOn() {
    if (window.ZIPCHAT_SEARCH_DEBUG) return true;
    try { return localStorage.getItem('zipchat_search_debug') === '1'; } catch (e) { return false; }
  }

  function log() {
    if (!debugOn()) return;
    console.log.apply(console, ['[zipchat-search]'].concat([].slice.call(arguments)));
  }

  // True only once Zipchat's hijack has attached to our trigger.
  // setupSearchHijacking sets that flag, and it only runs when search is
  // enabled, has selectors configured, and is visible on this device — so this
  // is the precise answer to "is AI Search live here, right now?". It also fails
  // safe: point the selector elsewhere, pause the widget, or switch search off
  // for a device, and nothing below touches a single event.
  //
  // Checked per event rather than at load, because the widget arrives async.
  function searchActive() {
    if (!(window.Zipchat && window.Zipchat._shadowRoot)) return false;
    var trigger = document.getElementById(TRIGGER_ID);
    return !!(trigger && trigger._zipchatSearchHijacked);
  }

  // Every listener goes through this, so the gate is applied in one place.
  function fieldFor(node) {
    if (!searchActive()) return null;
    if (!node || !node.closest) return null;
    var scope = node.closest(FIELD_SCOPE);
    if (!scope) return null;
    return scope.querySelector(FIELD_SELECTOR);
  }

  function setGlow(field, on) {
    var wrapper = field.closest('.search') || field.parentElement;
    if (!wrapper) return;
    if (on) wrapper.classList.add(GLOW_CLASS);
    else wrapper.classList.remove(GLOW_CLASS);
  }

  function overlayInput(root) {
    var overlay = root.getElementById('zipchat-search-overlay');
    if (!overlay) return null;
    // Reuse the card's input when the overlay is already expanded.
    var modalWrapper = overlay.querySelector('.zipchat-search-modal-wrapper');
    return (modalWrapper && modalWrapper.style.display === 'block')
      ? overlay.querySelector('.zipchat-search-modal-input')
      : overlay.querySelector('.zipchat-search-input');
  }

  // True when Zipchat took the query. False leaves the event alone, so the
  // theme's own search still answers rather than the visitor getting nothing.
  function handoff(query) {
    if (query.length < MIN_QUERY) { log('query too short', query); return false; }

    var root = window.Zipchat && window.Zipchat._shadowRoot;
    var trigger = document.getElementById(TRIGGER_ID);
    if (!root || !trigger) { log('widget not ready'); return false; }

    // The hijack's click handler is what holds widgetData and chatbotUrl.
    trigger.click();

    var input = overlayInput(root);
    if (!input) { log('overlay input missing'); return false; }

    input.value = query;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    log('handed off', query);
    return true;
  }

  function claim(event, field, source) {
    log('claiming', source);
    if (!handoff((field.value || '').trim())) return;

    event.preventDefault();
    event.stopPropagation();

    // The query lives in the overlay now, so leave the header field empty rather
    // than showing a term that no longer drives anything.
    field.value = '';
    if (field.blur) field.blur();
    setGlow(field, false);
  }

  // Focus lights the field up, so the visitor sees it is Zipchat's before typing
  // a character. Both event names are handled because focus does not bubble and
  // focusin does; adding the class twice is harmless.
  ['focus', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var field = fieldFor(event.target);
      if (field && field === event.target) setGlow(field, true);
    }, true);
  });

  document.addEventListener('focusout', function (event) {
    var field = fieldFor(event.target);
    if (field && field === event.target) setGlow(field, false);
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var field = fieldFor(event.target);
    if (field && field === event.target) claim(event, field, 'keydown');
  }, true);

  document.addEventListener('click', function (event) {
    var button = event.target.closest
      ? event.target.closest('[data-zipchat-search] .search__submit, .header__search .search__submit')
      : null;
    if (!button) return;
    var field = fieldFor(button);
    if (field) claim(event, field, 'click');
  }, true);

  // Covers anything that produces a real submit, such as a mobile keyboard's
  // "go" key, without relying on it: some storefront search apps navigate from
  // their own keydown handler and never fire submit at all.
  document.addEventListener('submit', function (event) {
    var field = fieldFor(event.target);
    if (field) claim(event, field, 'submit');
  }, true);

  // Set unconditionally, so "did this file run?" is answerable in the console
  // without the debug flag.
  window.__zipchatSearchHandler = 'installed';
  log('handler installed');
})();
